import * as React from "react";
import { SimpleContainerProps, SimpleContainerFTNodeOptions } from "./typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { getExtraClassNameBasedOnParameters } from "./utilityStyles";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { normalizeOptionsData, hasRenderableChilren } from "src/components/flexFramework/FlexTemplateNode";
import { SimpleContainerExpandAll } from "./SimpleContainerExpandAll";

export interface SimpleContainerContext {
  modulesHaveBorder?: boolean;
  collapseAll?: string;
  toggleCollapseAll?: any;
}

export enum SimpleContainerToggleState {
  ALL_EXPANDED = "expand",
  ALL_COLLAPSED = "collapse",
  NEITHER = "neither",
}

const defaultContext = {
  modulesHaveBorder: false,
  collapseAll: SimpleContainerToggleState.NEITHER,
};

export const SimpleContainerContext = React.createContext<SimpleContainerContext>(defaultContext);

const DEFAULT_WIDTH = "100";

export const SimpleContainer = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: SimpleContainerProps) => {
    const { context, blossomComponent, flexModuleModelStore, flexTemplateNode } = props;

    /* istanbul ignore next */
    if (!blossomComponent) {
      return null;
    }

    const options = normalizeOptionsData(
      flexTemplateNode,
      flexModuleModelStore.getModel(flexTemplateNode.id)
    ) as SimpleContainerFTNodeOptions;

    const {
      containerName,
      mobileWidth,
      title,
      width,
      innerTitle,
      a11yRegionLabel,
      a11yRegionAriaLabelTitle,
      tabletWidth,
      desktopWidth,
      hasBorder: modulesHaveBorder,
      flexComponentView,
    } = options;

    if (!hasRenderableChilren(flexTemplateNode, flexModuleModelStore)) {
      return null;
    }

    const [collapseAll, setCollapseAll] = React.useState(SimpleContainerToggleState.NEITHER);
    const toggleCollapseAll = (event: any) => {
      if (event === SimpleContainerToggleState.NEITHER) {
        setCollapseAll(event);
      } else if (
        event.target.value === SimpleContainerToggleState.ALL_COLLAPSED ||
        event.target.value === SimpleContainerToggleState.ALL_EXPANDED
      ) {
        setCollapseAll(event.target.value);
      }
    };

    const headingClass = "uitk-type-display-700 uitk-cell all-cell-1-1";
    const regionSpecificClasses = getRegionSpecificClasses(containerName);
    const regionSpecificLabel =
      title || innerTitle || a11yRegionAriaLabelTitle || a11yRegionLabel || getRegionSpecificLabels(containerName);
    const gridWidth = regionWidthToGridMap[width || DEFAULT_WIDTH];
    const gridTabletWidth = regionWidthToGridMap[tabletWidth || DEFAULT_WIDTH];
    const gridMobileWidth = regionWidthToGridMap[mobileWidth || DEFAULT_WIDTH];
    const flexContent = (
      <>
        {title && <h2 className={headingClass}>{title}</h2>}
        {innerTitle && <h2 className={headingClass}>{innerTitle}</h2>}
        {flexComponentView === "expand-collapse-subregions" && (
          <SimpleContainerExpandAll collapseAll={collapseAll} toggleCollapseAll={toggleCollapseAll} />
        )}
        <BlossomComponents blossomComponent={blossomComponent} context={context} />
      </>
    );

    const commonClassNames = ["uitk-grid uitk-cell", regionSpecificClasses, getExtraClassNameBasedOnParameters(options)]
      .filter((classes) => classes !== "")
      .join(" ");

    const regionA11yAttrs = {};
    if (regionSpecificLabel) {
      regionA11yAttrs["role"] = "region";
      regionA11yAttrs["aria-label"] = regionSpecificLabel;
    }

    return (
      <SimpleContainerContext.Provider value={{ modulesHaveBorder, collapseAll, toggleCollapseAll }}>
        <Viewport>
          {mobileWidth === "hidden" ? (
            <ViewSmall />
          ) : (
            <ViewSmall>
              <div
                className={`SimpleContainer ${
                  containerName === "dx-page-header" ? " elevation-4" : ""
                } ${commonClassNames} ${gridMobileWidth} `}
                {...regionA11yAttrs}
              >
                {flexContent}
              </div>
            </ViewSmall>
          )}

          {tabletWidth === "hidden" ? (
            <ViewMedium />
          ) : (
            <ViewMedium>
              <div
                className={`SimpleContainer ${
                  containerName === "dx-page-header" ? " elevation-4" : ""
                } ${commonClassNames} ${gridTabletWidth} `}
                {...regionA11yAttrs}
              >
                {flexContent}
              </div>
            </ViewMedium>
          )}

          {desktopWidth === "hidden" ? (
            <ViewLarge />
          ) : (
            <ViewLarge>
              <div
                className={`${commonClassNames} ${gridWidth} ${
                  containerName === "dx-page-header" ? "elevation-4" : " SimpleContainer"
                }`}
                {...regionA11yAttrs}
              >
                {flexContent}
              </div>
            </ViewLarge>
          )}
        </Viewport>
      </SimpleContainerContext.Provider>
    );
  })
);

/**
 * Return the classes corresponding to a specific region. This is
 * a temporary solution until Module groups are implemented
 * @param {string} regionName - Name of region
 * @return {string} classes to decorate region
 */
/* istanbul ignore next */
const getRegionSpecificClasses = (regionName?: string): string => {
  switch (regionName) {
    case "wizard":
      return "wizardHero all-b-margin-three whiteBackground";
    case "FullWidthWizard":
      return "wizardHero fullWidth";
    case "FullWidthBreadcrumbs":
      return "fullWidthBreadcrumbs";
    case "CallToActionGroup":
      return "callToActionGroup";
    case "all-b-padding-three":
      return "all-b-padding-three";
    case "StorefrontConfidence":
      return "storefrontConfidence";
    case "all-cell-1-3":
      return "subRegion all-cell-1-3";
    case "dx-sidebar-content":
      return "dx-sidebar-content";
    case "SEO links":
      return "SEOLinks";
    case "CenteredDestinationWizard":
      return "CenteredDestinationWizard";
    case "FullWidthConfidenceMessages":
      return "confidenceMessages fullWidth whiteBackground";
    case "NoBackgroundConfidenceMessages":
      return "confidenceMessages fullWidth";
    case "RecentlyBookedReviewedNoBorderBkg":
      return "recentlyBookedReviewed noBorder noBackground";
    case "NativeShowcase":
      return "all-y-padding-three";
    case "Weekend Getaways":
      return "all-t-padding-three";
    case "PropertyHeroImage":
      return "m-l-padding-three l-l-padding-three";
    case "PropertyReviewHeadline":
      return "m-r-padding-three l-r-padding-three";
    case "EdgePaddingWrapper":
      return "browser-edge-padding";
    case "HeroEditorial":
      return "heroEditorial";
  }

  return "";
};

const getRegionSpecificLabels = (regionName: string = ""): string => {
  switch (regionName) {
    case "banner_ad_1":
      return "Banner";
    case "geo_hotels_1":
      return "Explore cabin retreats";
    case "wizard":
    case "FullWidthWizard":
    case "CenteredDestinationWizard":
      return "Wizard";
    case "FullWidthBreadcrumbs":
      return "Breadcrumbs";
    case "CallToActionGroup":
      return "Call to action group";
    case "all-cell-1-3":
      return "Sub region";
    case "dx-sidebar-content":
      return "Sidebar";
    case "FullWidthConfidenceMessages":
    case "NoBackgroundConfidenceMessages":
      return "Message";
    case "RecentlyBookedReviewedNoBorderBkg":
      return "Recently booked";
    case "PropertyHeroImage":
      return "Property image";
    case "PropertyReviewHeadline":
      return "Property review headline";
  }
  return "";
};

const regionWidthToGridMap = {
  "25": "all-cell-1-4",
  "33": "all-cell-1-3",
  "50": "all-cell-1-2",
  "66": "all-cell-2-3",
  "75": "all-cell-3-4",
  "100": "all-cell-1-1",
};

export default SimpleContainer;
